/**
 * Global widget state using Zustand
 */

import { create } from 'zustand';
import type { WidgetState } from '../types';
import type { SearchResult, VoiceState, ResponseMeta } from '@ai360smartsearch/shared';
import { getApiClient } from '../services/api-client';
import { cache } from '../utils/cache';
import { getSetting } from '../utils/settings';

const MAX_HISTORY_ITEMS = 10;

export const useWidgetStore = create<WidgetState>((set, get) => ({
  // Initial state
  query: '',
  results: [],
  isSearching: false,
  searchError: null,
  voiceState: 'idle',
  transcript: '',
  theme: 'auto',
  isExpanded: false,
  searchHistory: loadSearchHistory(),
  responseType: null,
  meta: null,

  // Actions
  setQuery: (query: string) => {
    set({ query });
  },

  search: async (query: string) => {
    if (!query.trim()) {
      set({ searchError: new Error('Query cannot be empty') });
      return;
    }

    set({ isSearching: true, searchError: null, query });

    try {
      // Check cache first
      const cacheKey = `search:${query.toLowerCase()}`;
      const cachedResponse = cache.get<any>(cacheKey);

      if (cachedResponse) {
        set({ 
          results: cachedResponse.results,
          responseType: cachedResponse.type,
          meta: cachedResponse.meta,
          isSearching: false 
        });
        get().addToHistory(query);
        return;
      }

      // Get minResults from WordPress settings - check multiple locations
      const wpSettings = (window as any).ai360smartsearchSettings || {};
      const minResults = wpSettings.minResults || wpSettings.results?.minResults || 10;

      // Fetch from API using new searchRaw method
      const apiClient = getApiClient();
      const response = await apiClient.searchRaw(query, { maxResults: minResults });
      
      // Cache the full unified response
      cache.set(cacheKey, response);
      
      set({ 
        results: response.results,
        responseType: response.type,
        meta: response.meta,
        isSearching: false 
      });
      get().addToHistory(query);
    } catch (error: any) {
      set({ 
        searchError: error, 
        isSearching: false,
        results: [],
        responseType: null,
        meta: null,
      });
    }
  },

  clearResults: () => {
    set({ results: [], searchError: null, responseType: null, meta: null });
  },

  setVoiceState: (voiceState: VoiceState) => {
    set({ voiceState });
  },

  setTranscript: (transcript: string) => {
    set({ transcript });
  },

  addToHistory: (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    const { searchHistory } = get();
    
    // Remove duplicates and add to front
    const newHistory = [
      trimmedQuery,
      ...searchHistory.filter(q => q !== trimmedQuery)
    ].slice(0, MAX_HISTORY_ITEMS);

    set({ searchHistory: newHistory });
    saveSearchHistory(newHistory);
  },

  setTheme: (theme: 'light' | 'dark' | 'auto') => {
    set({ theme });
    
    // Apply theme to document
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      
      if (theme === 'auto') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
      } else {
        root.setAttribute('data-theme', theme);
      }
    }
  },

  toggleExpanded: () => {
    set(state => ({ isExpanded: !state.isExpanded }));
  },
}));

// Helper functions for localStorage
function loadSearchHistory(): string[] {
  if (typeof localStorage === 'undefined') return [];
  
  try {
    const stored = localStorage.getItem('ai360-search-history');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveSearchHistory(history: string[]): void {
  if (typeof localStorage === 'undefined') return;
  
  try {
    localStorage.setItem('ai360-search-history', JSON.stringify(history));
  } catch {
    // Ignore storage errors
  }
}
